import React from 'react';
import { Info } from 'lucide-react';

export interface MetricCardSegment {
  label: string;
  value: number;
  color: string;
}

interface MetricCardProps {
  title: string;
  value: string;
  subValue?: string;
  secondaryValue?: string;
  change?: number;
  changeLabel?: string;
  changeValueLabel?: string;
  periodLabel?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  tooltip?: string;
  accentColor?: string;
  showSparkline?: boolean;
  sparklineDirection?: 'up' | 'down';
  sparklineColor?: string;
  stackedBarSegments?: MetricCardSegment[];
  currencySymbol?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subValue,
  secondaryValue,
  change,
  changeLabel,
  changeValueLabel,
  periodLabel,
  icon,
  trend,
  tooltip,
  accentColor,
  showSparkline,
  sparklineDirection,
  sparklineColor,
  stackedBarSegments,
  currencySymbol = '',
}) => {
  const isPositive = change !== undefined && change >= 0;
  const trendColor = trend === 'down' ? 'text-rose-400' : isPositive ? 'text-emerald-400' : 'text-rose-400';
  const arrowSymbol = trend === 'down' ? '↓' : '↑';
  const valueChangeLabel = changeValueLabel ?? (change !== undefined ? `${change > 0 ? '+' : ''}${change.toFixed(2)}%` : undefined);
  const accentGlow = accentColor ? { boxShadow: `0 10px 28px ${accentColor}18` } : undefined;
  const iconAccent = accentColor
    ? { background: `${accentColor}1f`, color: accentColor, borderColor: `${accentColor}33` }
    : undefined;
  const cardAccentStyle = accentGlow;
  const resolvedSparklineDirection = sparklineDirection ?? trend ?? 'up';
  const sparklineStroke =
    sparklineColor ??
    (resolvedSparklineDirection === 'down'
      ? '#fb7185'
      : resolvedSparklineDirection === 'up'
      ? '#34d399'
      : accentColor || '#38bdf8');
  const sparklinePath =
    resolvedSparklineDirection === 'down'
      ? 'M 3 4 L 28 9 L 52 14 L 76 19 L 97 24'
      : 'M 3 24 L 28 19 L 52 14 L 76 9 L 97 4';
  const positiveStackedSegments = (stackedBarSegments || []).filter((segment) => segment.value > 0);
  const stackedBarTotal = positiveStackedSegments.reduce((sum, segment) => sum + segment.value, 0);
  const sortedStackedSegments = [...positiveStackedSegments].sort((a, b) => b.value - a.value);
  const formatSegmentValue = (amount: number) =>
    `${currencySymbol}${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div
      className="bg-[#1e1e26] p-5 min-h-[158px] rounded-xl border border-white/5 shadow-lg relative overflow-visible group transition-all duration-300 hover:border-white/10"
      style={cardAccentStyle}
    >
      {accentColor && (
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{ background: `radial-gradient(circle at 20% 20%, ${accentColor}21, transparent 45%)` }}
        />
      )}

      <div className="relative z-10">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center space-x-2.5">
            {icon && (
              <div className="p-2 rounded-lg border border-white/5" style={iconAccent}>
                {icon}
              </div>
            )}
            <div className="flex items-center space-x-2">
              <h3 className="text-slate-300 text-sm font-semibold">{title}</h3>
              <div className="relative group/tooltip flex items-center">
                <span className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 p-1 text-slate-400 hover:text-slate-200 transition-colors">
                  <Info
                    size={14}
                    className="cursor-help"
                    aria-label={`${title} info`}
                  />
                </span>
                {tooltip && (
                  <div className="tooltip-content absolute left-0 top-full mt-2 w-56 max-w-[14rem] bg-black/90 text-xs text-slate-200 p-3 rounded-lg border border-white/10 opacity-0 group-hover/tooltip:opacity-100 group-focus-within/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none shadow-xl whitespace-normal break-words text-left z-20">
                    {tooltip}
                  </div>
                )}
              </div>
            </div>
          </div>

          {periodLabel && <span className="text-sm text-slate-500 font-medium">{periodLabel}</span>}
        </div>

        {stackedBarTotal > 0 ? (
          <div className="mb-4 flex items-end gap-3">
            <div
              className="flex h-20 w-16 flex-col-reverse overflow-visible rounded-lg border border-white/10 bg-black/20 shadow-inner"
              aria-label={`${title} asset split`}
            >
              {sortedStackedSegments.map((segment) => {
                const percentage = (segment.value / stackedBarTotal) * 100;

                return (
                  <div
                    key={segment.label}
                    className="group/segment relative min-h-[4px] cursor-help transition-all duration-200 hover:brightness-125"
                    style={{ height: `${percentage}%`, backgroundColor: segment.color }}
                    aria-label={`${segment.label}: ${formatSegmentValue(segment.value)} (${percentage.toFixed(1)}%)`}
                  >
                    <div className="pointer-events-none absolute left-full top-1/2 z-30 ml-3 min-w-44 -translate-y-1/2 rounded-lg border border-white/10 bg-black/90 p-3 text-left text-xs text-slate-200 opacity-0 shadow-xl transition-opacity duration-200 group-hover/segment:opacity-100">
                      <div className="mb-1 flex items-center gap-2 font-bold text-white">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: segment.color }}
                        />
                        {segment.label}
                      </div>
                      <div>{formatSegmentValue(segment.value)}</div>
                      <div className="text-slate-400">{percentage.toFixed(1)}% of assets</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid flex-1 grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-400">
              {sortedStackedSegments.slice(0, 4).map((segment) => (
                <div key={segment.label} className="flex min-w-0 items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: segment.color }} />
                  <span className="truncate">{segment.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : showSparkline && (
          <div className="mb-4">
            <svg viewBox="0 0 100 28" className="h-14 w-full" preserveAspectRatio="none" aria-hidden="true">
              <path
                d={sparklinePath}
                fill="none"
                stroke={sparklineStroke}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}

        <div className="flex flex-col">
          {valueChangeLabel && (
            <div className={`mb-2 flex items-center gap-2 text-sm font-bold ${trendColor}`}>
              <span className="text-2xl leading-none">{arrowSymbol}</span>
              <span>{valueChangeLabel}</span>
              {change !== undefined && <span>({change.toFixed(0)}%)</span>}
            </div>
          )}

          <span className="break-words text-[clamp(1.55rem,2vw,1.875rem)] font-bold text-white mb-2 tracking-tight leading-tight">
            {value}
          </span>

          <div className="flex items-center flex-wrap gap-2 mt-1">
            {change !== undefined && !valueChangeLabel && (
              <div className={`flex items-center text-sm font-bold ${trendColor}`}>
                {change > 0 ? '+' : ''}{change.toFixed(2)}%
              </div>
            )}

            {(subValue || changeLabel) && (
              <span className={`text-sm font-medium ${subValue?.includes('invested') || changeLabel ? 'text-slate-500' : 'text-slate-400'}`}>
                {subValue || changeLabel}
              </span>
            )}

            {secondaryValue && (
              <span className="text-xs font-medium text-slate-500">
                {secondaryValue}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricCard;
